/**
 * The **content page** — the fourth page shape (`docs/site-section-system.md`
 * §9.2 and §9.6 decision 5): a standalone page of words at its own address.
 * Pink Perry VI runs four of them by hand (`/story`, `/faq`, `/occasions`,
 * and the shop's own page) and today the platform can only offer them as
 * anchors on the home page, which is why four templates and every dressed
 * tenant carry an FAQ band the matrix says is not a home band.
 *
 * This file holds the half of that page which both surfaces must agree on
 * letter for letter: **what a slug may be** and **what the document under it
 * looks like**. The renderers stay in `apps/site`, the refusal sentences stay
 * in `apps/admin-api`, and the route itself is `./pages.ts` beside the three
 * that were already there — this is the vocabulary, not the plumbing.
 *
 * ## The shape, in one paragraph
 *
 * `branding.pages` is a map from slug to `{ title, sections }`. The title is
 * **localized tenant content** exactly like a nav label (decision 6): a string
 * or an `{en, es}` map, resolved by `localize`, never translated by us. The
 * sections are an arrangement like the home page's, with one extra rule the
 * validator enforces and this file states: **every block on a content page
 * must list `'content'` in its `pages`** (`sectionsForPage('content')`). A
 * content page is not a second home page — there is no hero requirement, no
 * catalog band, and no booking control; it is the words, and the frame around
 * them is the same masthead and footer every other page wears.
 */
/* ------------------------------------------------------------------ */
/* The slug                                                            */
/* ------------------------------------------------------------------ */
/**
 * What a content-page slug may be, once written. The same alphabet as
 * `CATEGORY_SLUG_PATTERN` — lower-case, digits, single interior hyphens — for
 * the reason that rule gives: an address is a thing a person reads out.
 *
 * The difference from a category slug is where it comes from. A category slug
 * is **derived** from a name the catalog already holds, so the rule there is a
 * function and a name that transliterates to nothing simply has no page. A
 * content page's slug is **chosen** by the operator and is the page's only
 * identity, so the rule here is a predicate: there is nothing to derive it
 * from and nothing that could rename it behind the operator's back.
 */
export const CONTENT_PAGE_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/**
 * Forty characters, not the category rule's sixty.
 *
 * A category slug carries a name somebody else wrote ("Sunset Sailing
 * Adventures & Charters"); a content-page slug is typed into a box by the
 * person who will read the URL out loud on the phone. `occasions`,
 * `what-to-bring`, `gift-cards` — the longest real one in the roster is
 * seventeen characters, and forty is two or three words past that.
 */
export const CONTENT_PAGE_SLUG_MAX = 40;
/**
 * **Slugs a content page may never take, because a route already owns them.**
 *
 * `visits`, `cart` and `track` are the built-in affordances a tenant may name
 * and place but never remove (`./pages.ts`, `BUILTIN_PATHS`); `photos` is the
 * fourth utility room, in the bundle's route table without a nav target of its
 * own (a guest reaches it from the link on their ticket); `c`, `i` and `p` are
 * the first segments of the category, item and content routes themselves
 * (`PAGE_PATHS`). A tenant who wrote a page at `c` would publish a document
 * that renders at an address the router reads as "a category page with no
 * slug" — accepted, stored, and never once shown to a guest, which is the
 * failure decision 7 of `guest-pages-design.md` refuses to ship twice.
 *
 * So the refusal is here, in the predicate both surfaces call, rather than in
 * whichever of them happened to think of it. The list is short and it is the
 * whole of what the URL space has spoken for: a *page* slug never collides
 * with a *category* slug, because they live under different prefixes.
 */
export const CONTENT_PAGE_RESERVED_SLUGS = [
    'visits',
    'cart',
    'track',
    'photos',
    'c',
    'i',
    'p',
];
/**
 * How many content pages one tenant may publish.
 *
 * Twelve, against the nav's seven: a page does not have to be linked from the
 * bar — a footer row, a CTA or another page's prose can reach it — but a
 * business with more than a dozen pages of words on a booking site has
 * written a brochure, and every one of these rides in the one branding row
 * `guest-api` reads in full on every page load. The ceiling that actually
 * binds is still `MAX_DOCUMENT_BYTES` on the whole row; this is the honest
 * number to put in front of an operator before they hit it.
 */
export const CONTENT_PAGES_MAX = 12;
/** The page's heading. Eighty characters is a headline, not a paragraph. */
export const CONTENT_PAGE_TITLE_MAX = 80;
/**
 * Bands on one content page. Thirty matches the home arrangement's own
 * ceiling — a content page is arranged by the same editor, out of the same
 * registry, and there is no reason for the number to differ by page.
 */
export const CONTENT_PAGE_SECTIONS_MAX = 30;
/**
 * Is this a slug a content page may live at?
 *
 * The length, the alphabet, and the reserved list, in that order. `false` is
 * what both consumers act on and they act on it differently, the way the rest
 * of the contract runs: the bundle drops the page with a console warning (one
 * static build serves every hostname), and `admin-api` answers `400` naming
 * which of the three rules was broken.
 */
export function isContentPageSlug(value) {
    if (value.length === 0 || value.length > CONTENT_PAGE_SLUG_MAX)
        return false;
    if (!CONTENT_PAGE_SLUG_PATTERN.test(value))
        return false;
    return !CONTENT_PAGE_RESERVED_SLUGS.includes(value);
}
//# sourceMappingURL=content.js.map